/**
 * Usage Trends Chart — Pure HTML/CSS implementation
 *
 * Shows daily/hourly/weekly usage trends as a stacked bar chart + table.
 * Fetches its own data via fetchUsageTrends().
 */

import { useState, useEffect } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import {
  fetchUsageTrends,
  type MetricsFilters,
  type UsageTrendBucket,
} from '@/services/metricsAPI';

interface UsageTrendsChartProps {
  filters: MetricsFilters;
  token: string;
  bucket?: 'hour' | 'day' | 'week';
}

export function UsageTrendsChart({
  filters,
  token,
  bucket = 'day',
}: UsageTrendsChartProps): JSX.Element {
  const { t } = useTranslation();
  const [data, setData] = useState<UsageTrendBucket[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchUsageTrends(filters, token, bucket);
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) {
          setError('Failed to load trend data');
          console.error('[UsageTrendsChart] fetch error:', err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [filters, token, bucket]);

  if (loading) {
    return <div className="chart-empty-state">{t('common.loading')}</div>;
  }

  if (error || data.length === 0) {
    return <div className="chart-empty-state">{error ?? t('admin.metrics.noChartData')}</div>;
  }

  const maxOps = Math.max(...data.map((b) => b.total_operations), 1);
  const maxTokens = Math.max(...data.map((b) => b.total_tokens), 1);
  const maxUsers = Math.max(...data.map((b) => b.unique_users), 1);

  return (
    <div className="space-y-4">
      {/* Legend */}
      <div className="flex gap-4 text-xs text-white/60">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-purple-400/80" />
          {t('admin.metrics.chartOperations')}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-cyan-400/80" />
          {t('admin.metrics.chartTokens')}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-green-400/80" />
          {t('admin.metrics.chartUsers')}
        </span>
      </div>

      {/* Bar chart area */}
      <div className="flex items-end gap-1 h-40 border-b border-white/10 pb-1">
        {data.map((b, idx) => {
          const opsPct = (b.total_operations / maxOps) * 100;
          const tokensPct = (b.total_tokens / maxTokens) * 100;
          const usersPct = (b.unique_users / maxUsers) * 100;

          return (
            <div
              key={`${b.date}-${idx}`}
              className="flex-1 flex items-end gap-px group relative"
              title={`${b.date}\nOps: ${b.total_operations}\nTokens: ${b.total_tokens.toLocaleString()}\nUsers: ${b.unique_users}`}
            >
              <div
                className="flex-1 bg-purple-400/70 rounded-t min-h-[2px] transition-all duration-300"
                style={{ height: `${Math.max(opsPct, 2)}%` }}
              />
              <div
                className="flex-1 bg-cyan-400/70 rounded-t min-h-[2px] transition-all duration-300"
                style={{ height: `${Math.max(tokensPct, 2)}%` }}
              />
              <div
                className="flex-1 bg-green-400/70 rounded-t min-h-[2px] transition-all duration-300"
                style={{ height: `${Math.max(usersPct, 2)}%` }}
              />
            </div>
          );
        })}
      </div>

      {/* Date labels */}
      <div className="flex gap-1">
        {data.map((b, idx) => (
          <div
            key={`label-${b.date}-${idx}`}
            className="flex-1 text-[9px] text-white/30 text-center truncate"
          >
            {b.date.slice(5)}
          </div>
        ))}
      </div>

      {/* Summary table */}
      <div className="overflow-x-auto -mx-1">
        <table className="w-full text-xs text-white/60">
          <thead className="text-white/40 border-b border-white/10">
            <tr>
              <th className="text-left py-1 px-1">{t('admin.metrics.date')}</th>
              <th className="text-right py-1 px-1">{t('admin.metrics.chartOperations')}</th>
              <th className="text-right py-1 px-1">{t('admin.metrics.chartTokens')}</th>
              <th className="text-right py-1 px-1">{t('admin.metrics.chartUsers')}</th>
              <th className="text-right py-1 px-1">{t('admin.metrics.success')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {data.slice(-7).map((b, idx) => (
              <tr key={`row-${b.date}-${idx}`} className="hover:bg-white/5">
                <td className="py-1 px-1 font-mono">{b.date}</td>
                <td className="py-1 px-1 text-right">{b.total_operations}</td>
                <td className="py-1 px-1 text-right">{b.total_tokens.toLocaleString()}</td>
                <td className="py-1 px-1 text-right">{b.unique_users}</td>
                <td className="py-1 px-1 text-right">
                  <span
                    className={
                      b.success_count / (b.success_count + b.failure_count || 1) >= 0.95
                        ? 'text-green-400'
                        : 'text-yellow-400'
                    }
                  >
                    {b.success_count}/{b.success_count + b.failure_count}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
